import { NodeUtils } from 'Editor/services/DataManager';
import { StylesDataMapper } from '../../Manipulators/Common/Styles/StylesDataMapper';
import { StylesUtils } from './StylesUtils';
import deepEqual from 'deep-equal';
import { Logger } from '_common/services';

export class DocumentStylesHandler {
  private documentStylesManager: Editor.Data.Styles.DocumentStylesManager | undefined;

  private editionContext: Editor.Edition.Context;
  private stylesContext: Editor.Edition.Styles.Context;

  constructor(
    editionContext: Editor.Edition.Context,
    stylesContext: Editor.Edition.Styles.Context,
  ) {
    this.editionContext = editionContext;
    this.stylesContext = stylesContext;

    this.documentStylesManager = this.editionContext.DataManager?.styles.documentStyles;

    this.handleResponseDeleteDocumentStyle = this.handleResponseDeleteDocumentStyle.bind(this);
    this.handleResponseResetTemplateStyle = this.handleResponseResetTemplateStyle.bind(this);
  }

  initialize() {
    //TODO think of a better solution for this event responses
    this.editionContext.DataManager?.on(
      'DOCUMENT_STYLES_DELETE_RESPONSE',
      this.handleResponseDeleteDocumentStyle,
    );
    this.editionContext.DataManager?.on(
      'DOCUMENT_STYLES_RESET_RESPONSE',
      this.handleResponseResetTemplateStyle,
    );
  }

  destroy() {
    this.editionContext.DataManager?.off(
      'DOCUMENT_STYLES_DELETE_RESPONSE',
      this.handleResponseDeleteDocumentStyle,
    );
    this.editionContext.DataManager?.off(
      'DOCUMENT_STYLES_RESET_RESPONSE',
      this.handleResponseResetTemplateStyle,
    );
  }

  getDocumentStyleStatusFromRange(
    range: Editor.Selection.JsonRange,
  ): Editor.Edition.Styles.DocumentStyleStatus | undefined {
    if (!this.documentStylesManager?.loaded || !this.stylesContext.stylesUtils) {
      return;
    }

    let styleStatus: Editor.Edition.Styles.DocumentStyleStatus | undefined;

    const contentStyles = this.stylesContext.stylesUtils.getStylesAppliedToSelection(range, {
      includeOtherStyles: true,
    });

    let selectedDocStyle = contentStyles.s?.[0];
    if (selectedDocStyle != null) {
      styleStatus = {
        id: selectedDocStyle,
        changed: false,
        p: {},
      };

      const mappedDocStypeProps: Editor.Data.Structure.DocumentStyleProperties = {};

      // mapp content styles to document styles
      const styleKeys = Object.typedKeys(contentStyles);
      for (let s = 0; s < styleKeys.length; s++) {
        const style = styleKeys[s];
        if (StylesUtils.isDocumentStyle(style)) {
          StylesDataMapper.DocumentStyleProperties[style].map(contentStyles, mappedDocStypeProps);
        }
      }

      //check if mappedDocStypeProps are different from currentDocStyle props
      const currentDocStyle = this.documentStylesManager.style(styleStatus.id);

      if (this.stylesContext.debug) {
        Logger.debug(
          '[DocumentStylesHandler] getDocumentStyleStatusFromRange',
          mappedDocStypeProps,
          currentDocStyle,
        );
      }

      if (currentDocStyle) {
        // filter unique doc style props
        const props = [
          ...Object.typedKeys(mappedDocStypeProps),
          ...Object.typedKeys(currentDocStyle.extendedP),
        ];
        const uniqueDocStyleProps = Array.from(new Set(props));

        const checkedDocStyleProps: Editor.Data.Structure.DocumentStyleProperties = {};
        for (let z = 0; z < uniqueDocStyleProps.length; z++) {
          const docStyle = uniqueDocStyleProps[z];

          let changed = false;

          let value = currentDocStyle.extendedP?.[docStyle];

          if (docStyle === 'color' || docStyle === 'bg') {
            value = NodeUtils.processColor(value);
          }

          if (Array.isArray(value) && (value.length || mappedDocStypeProps[docStyle]?.length)) {
            // NOTE: avoid comparing undefined with empty array, both mean no changes
            const valuesEqual =
              value.length === mappedDocStypeProps[docStyle]?.length &&
              value.every((v, i) =>
                deepEqual(v, mappedDocStypeProps[docStyle]?.[i], { strict: false }),
              );
            if (!valuesEqual) {
              changed = true;
            }
          } else if (typeof value === 'object') {
            const valuesEqual = deepEqual(value, mappedDocStypeProps[docStyle], { strict: false });
            if (!valuesEqual) {
              changed = true;
            }
          } else if (value != mappedDocStypeProps[docStyle]) {
            changed = true;
          }

          if (checkedDocStyleProps[docStyle] == null) {
            if (changed) {
              styleStatus.changed = true;

              styleStatus.p[docStyle] = mappedDocStypeProps[docStyle];
            }

            checkedDocStyleProps[docStyle] = mappedDocStypeProps[docStyle];
          } else if (checkedDocStyleProps[docStyle] !== mappedDocStypeProps[docStyle]) {
            delete styleStatus.p[docStyle];
            if (Object.keys(styleStatus.p).length === 0) {
              styleStatus.changed = false;
            }

            checkedDocStyleProps[docStyle] = '';
          }
        }
      }
    }

    if (this.stylesContext.debug) {
      Logger.debug('[DocumentStylesHandler] getDocumentStyleStatusFromRange', styleStatus);
    }

    return styleStatus;
  }

  async updateDocumentStyleFromSelection(
    styleId: string,
    options?: Editor.Edition.StylesCommandOptions,
  ) {
    if (this.stylesContext.debug) {
      logger.debug('DocumentStyles updateDocumentStyleFromSelection', styleId, options);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'UPDATE_DOCUMENT_STYLE_COMMAND',
      styleId,
      options,
    );

    if (command) {
      await command.exec();
    }
  }

  async applyDocumentStyleToSelection(
    styleId: string,
    rangeToApply?: Editor.Selection.JsonRange,
    options?: Editor.Edition.StylesCommandOptions,
  ) {
    if (this.stylesContext.debug) {
      logger.debug('DocumentStyles applyDocumentStyleToSelection', styleId, rangeToApply, options);
    }

    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'APPLY_DOCUMENT_STYLE_COMMAND',
      styleId,
      rangeToApply,
      options,
    );

    if (command) {
      await command.exec();
    }
  }

  async renameDocumentStyle(
    styleId: string,
    newName: string,
    options?: Editor.Edition.StylesCommandOptions,
  ) {
    if (this.stylesContext.debug) {
      logger.debug('DocumentStyles renameDocumentStyle', styleId, newName, options);
    }
    if (!this.editionContext.commandFactory) {
      return;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'RENAME_DOCUMENT_STYLE_COMAND',
      styleId,
      newName,
      options,
    );

    if (command) {
      await command.exec();
    }
  }

  async createDocumentStyle(
    baseStyleData: string | Partial<Editor.Data.Structure.DocumentStyleData>,
    options: Editor.Edition.StylesCommandOptions & { applyDocumentStyle?: boolean } = {},
  ) {
    if (this.stylesContext.debug) {
      logger.debug('DocumentStyles createDocumentStyle', baseStyleData, options);
    }
    if (!this.editionContext.commandFactory) {
      return;
    }

    let createOptions: Editor.Edition.StylesCommandOptions = { ...options };
    if (options.applyDocumentStyle) {
      createOptions.createPatch = false;
      createOptions.untrackedActionWarning = false;
    }

    let command = this.editionContext.commandFactory.getCommand(
      'CREATE_DOCUMENT_STYLE_COMMAND',
      baseStyleData,
      createOptions,
    );

    if (command) {
      await command.exec();

      const createdDocumentStyleData = command.getCreatedDocumentStyleData();

      if (options.applyDocumentStyle && createdDocumentStyleData) {
        await this.applyDocumentStyleToSelection(createdDocumentStyleData.id, undefined, options);
      }

      return createdDocumentStyleData;
    }
  }

  deleteDocumentStyle(styleId: string) {
    if (this.stylesContext.debug) {
      logger.debug('DocumentStyles deleteDocumentStyle', styleId);
    }
    return this.editionContext.DataManager?.styles.deleteDocumentStyle(styleId);
  }

  private handleResponseDeleteDocumentStyle({
    success,
    styleId,
    style,
  }: {
    success: boolean;
    styleId: string;
    style: any;
  }) {
    if (this.stylesContext.debug) {
      logger.debug('DocumentStyles handleResponseDeleteDocumentStyle', success, styleId, style);
    }

    let notifierData: Utils.ToastProps | undefined;
    if (success) {
      notifierData = {
        variant: 'success',
        title: 'PARAGRAPH_STYLE_REMOVED_TITLE',
        titleValues: { style: style.n },
        message: 'PARAGRAPH_STYLE_REMOVED_MESSAGE',
        messageValues: { style: style.n },
      };
    } else {
      const styleName = this.editionContext.DataManager?.styles.documentStyles?.style(styleId).n;
      if (styleName) {
        notifierData = {
          variant: 'error',
          title: 'PARAGRAPH_STYLE_REMOVED_TITLE_ERROR',
          titleValues: { style: styleName },
          message: 'PARAGRAPH_STYLE_REMOVED_MESSAGE_ERROR',
          messageValues: { style: styleName },
        };
      }
    }

    if (notifierData) {
      this.editionContext.notifier(notifierData);
    }
  }

  resetTemplateStyle(styleId: string) {
    if (this.stylesContext.debug) {
      logger.debug('DocumentStyles resetTemplateStyle', styleId);
    }
    return this.editionContext.DataManager?.styles.resetTemplateStyle(styleId);
  }

  private handleResponseResetTemplateStyle({
    success,
    styleId,
    style,
  }: {
    success: boolean;
    styleId: string;
    style: any;
  }) {
    if (this.stylesContext.debug) {
      logger.debug('DocumentStyles handleResponseResetTemplateStyle', success, styleId, style);
    }

    let notifierData: Utils.ToastProps | undefined;

    if (success) {
      notifierData = {
        variant: 'success',
        title: 'PARAGRAPH_STYLE_RESETED_TITLE',
        titleValues: { style: style.n },
        message: 'PARAGRAPH_STYLE_RESETED_MESSAGE',
        messageValues: { style: style.n },
      };
    } else {
      const styleName = this.editionContext.DataManager?.styles.documentStyles?.style(styleId).n;
      if (styleName) {
        notifierData = {
          variant: 'success',
          title: 'PARAGRAPH_STYLE_RESETED_TITLE',
          titleValues: { style: styleName },
          message: 'PARAGRAPH_STYLE_RESETED_MESSAGE',
          messageValues: { style: styleName },
        };
      }
    }

    if (notifierData) {
      this.editionContext.notifier(notifierData);
    }
  }
}
